import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationCost } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, Box, Button, NonCancelableCustomEvent, Select, SelectProps, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { awsRightSizePolicyIdToString } from '../../../common/RightSizingPolicyName';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../../contexts/PortfolioContext';
import { TcoContext } from '../../../contexts/TcoContext';
import PreviewBanner from '../../common/PreviewBanner';
import SavingsSummary, { SummaryStats } from '../SavingsSummary';
import { ApplicationCostColumnDefinition } from './ApplicationCostColumnDefinition';

type Props = FlashContextInterface;

const tcoService = new TcoService();

function ApplicationCostSummaryPage({ flash }: Props) {
    const isMounted = useIsMounted();

    const { assessment, assessmentMetadatas, tcoLoading } = useContext(TcoContext);
    const { portfolio } = useContext(PortfolioContext);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const history = useHistory();

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;

    const [ loading, setLoading ] = useState(false);
    const [ applicationCosts, setApplicationCosts ] = useState<ApplicationCost[]>([]);
    const [ columnDefinitions, setColumnDefinitions ] = useState<ColumnDefinition<ApplicationCost>[]>([]);
    const [ currency, setCurrency ] = useState<string>('');
    const [ tcoDuration, setTcoDuration ] = useState(0);
    const [ tcoRegion, setTcoRegion ] = useState('');
    const [ awsRightSizePolicyName, setAwsRightSizePolicyName ] = useState('');
    const [ assessmentList, setAssessmentList] = useState<SelectProps.Options>([]);
    const [ selectedAssessment, setSelectedAssessment ] = useState<SelectProps.Option | null>(null);
    const [ recalculateTco, setRecalculateTco ] = useState(false);
    const [ selectedApplications, setSelectedApplications ] = useState<ApplicationCost[]>([]);
    const emptySummary: SummaryStats = {
        awsCost: 0,
        onPremisesCost: 0,
        savings: 0,
    };
    const [summaryTotals, setSummaryTotals] = useState<SummaryStats>(emptySummary);
    const { panelHandler } = useContext(HelpPanelContext);

    async function loadData() {
        setLoading(true);
        try {
            if (tcoAssessmentId && portfolioId && assessment && assessmentMetadatas?.length > 0) {
                const { tcoAssessmentIds } = portfolio;
                // If there are no assessment Ids, then TCO has never been calculated
                if (tcoAssessmentIds) {
                    const newAssessmentList = assessmentMetadatas.map((assessment: any) => {
                        return {
                            label: assessment.name || constants.DEFAULT_ASSESSMENT_NAME,
                            value: assessment.id,
                        };
                    });
                    const activeAssessment = newAssessmentList.find((assessment: SelectProps.Option) => assessment.value === tcoAssessmentId) ?? null;
                    const {
                        applicationCosts: allApplicationCosts,
                        awsRightSizePolicy: awsRightSizePolicyFromAssessment, currency: portfolioCurrency, duration, region } =
                        await tcoService.getApplicationCentricCostSummary(activeAssessment?.value);
                    const awsRightSizePolicy = awsRightSizePolicyFromAssessment ??
                        (await tcoService.getDescription('')).configuration.properties.find(property => property.identifier === 'awsEC2Pricing')
                            ?.properties.find(subProperty => subProperty.identifier === 'awsRightSizePolicy')?.defaultValue ?? '';
                    if (!isMounted()) {
                        return;
                    }
                    setSelectedAssessment(activeAssessment);
                    setAssessmentList(newAssessmentList);
                    setApplicationCosts(allApplicationCosts);
                    setCurrency(portfolioCurrency);
                    setTcoDuration(duration);
                    setTcoRegion(region);
                    setAwsRightSizePolicyName(awsRightSizePolicyIdToString(awsRightSizePolicy));
                    setColumnDefinitions(ApplicationCostColumnDefinition(portfolioCurrency, goToApplicationCost));
                    
                    if (allApplicationCosts.length > 0 && (!allApplicationCosts[0].aws || allApplicationCosts[0].aws.totalCost === undefined)) {
                        setRecalculateTco(true);
                    } else {
                        setRecalculateTco(false);
                        updateSummaryTotals(allApplicationCosts);
                    }
                } else {
                    setRecalculateTco(true);
                }
            }
        } catch (e) {
            if (!isMounted()) {
                return;
            }
            flash.alert(FlashType.error, constants.FAILED_TO_LOAD_APP_COST_SUMMARY, (e as Error).message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_COST_SUMMARY);
    }, [ panelHandler ]);

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId, assessment, assessmentMetadatas ]);

    const updateSummaryTotals = (applications: readonly ApplicationCost[]) => {
        if (applications) {
            const summaryTotal = applications.reduce((summary: SummaryStats, applicationCost: ApplicationCost) => {
                summary.awsCost += applicationCost.aws.totalCost;
                summary.onPremisesCost += applicationCost.onPremises.totalCost;
                return summary;
            }, emptySummary);
            summaryTotal.savings = summaryTotal.onPremisesCost ? 1 - (summaryTotal.awsCost / summaryTotal.onPremisesCost) : 0;
            setSummaryTotals(summaryTotal);
        }
    };

    const defaultSortingState: SortingState<ApplicationCost> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'applicationId',
        },
    };

    const goToApplicationCost = (applicationId: string): void => {
        history.push(`/portfolio/${portfolioId}/management/application-detail/${applicationId}/cost`);
    };

    const applicationCostTable = applicationCosts.length ? (
        <AWSCATTable 
            resourceName={constants.APPLICATIONS}
            items={applicationCosts}
            columnDefinitions={columnDefinitions}
            enableAutoSorting
            selectionType='multi'
            onSelectionChange={setSelectedApplications as () => void}
            defaultSortBy={defaultSortingState}
            autoGenerateVisibleContent
            useDefaultDownload
            downloadFileName={constants.APPLICATION_COST_SUMMARY}
        />
    ) : null;

    const summaryButtonClick = () => {
        if (selectedApplications.length) {
            updateSummaryTotals(selectedApplications);
        } else {
            updateSummaryTotals(applicationCosts);
        }
    };

    const summaryTotalUpdateButton = (
        <Box float='right'>
            <Button className='awsui-util-f-r' id='btn-update-tco-summary' onClick={summaryButtonClick} variant='primary'>
                {constants.UPDATE_SUMMARY_TOTALS}
            </Button>
        </Box>
    );

    const onAssessmentChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        const newTcoAssessmentId = event.detail.selectedOption.value;
        history.push(`/portfolio/${portfolioId}/tco/${newTcoAssessmentId}/application-summary`);
        setLoading(true);
    };

    const assessmentDropdown = (
        <div>
            <Select
                className='awsui-util-f-r'
                selectedOption={selectedAssessment}
                options={assessmentList}
                onChange={onAssessmentChange}
                disabled={assessmentList.length === 1}
            />
        </div>
    );

    const content = !loading && !recalculateTco && selectedAssessment && (
        <SpaceBetween size='l'>
            <PreviewBanner />
            {assessmentDropdown}
            <SavingsSummary
                awsRightSizePolicyName={awsRightSizePolicyName}
                currency={currency}
                duration={tcoDuration}
                region={tcoRegion}
                summaryTotals={summaryTotals}
            />
            {summaryTotalUpdateButton}
            {applicationCostTable}
        </SpaceBetween>
    );

    const recalculateTcoMessage = !loading && recalculateTco && (
        <Alert header={constants.RECALCULATE_TCO_MESSAGE_HEADER}>
            {constants.RECALCULATE_TCO_MESSAGE}
        </Alert>
    );

    const buildApplicationSummaryContent = (): JSX.Element => {
        return (
            <div className='content' data-testid='applicationCostSummaryPage'>
                <div className='awsui-row'>
                    {loading ? <Spinner size='big' /> : null}
                    {recalculateTcoMessage}
                    {content}
                </div>
            </div>
        );
    };

    return !tcoLoading && !loading ? 
        buildApplicationSummaryContent()
        : <Spinner size='large' />;
}

export default withFlashContext(ApplicationCostSummaryPage);
